export const HOUR_HEIGHT = 48
export const ONE_MINUTE_MS = 60 * 1000
export const MIN_DISPLAY_MS = 30 * 60 * 1000
export const MIN_LANE_WIDTH = 70
// Height (px) needed to fit the time row + title row in an event block.
export const MIN_MULTI_DAY_HEIGHT = 44

const effectiveEnd = (start, end) => Math.max(end, start + MIN_DISPLAY_MS)

export const buildOverlapClusters = (events) => {
  const sorted = [...events].sort((a, b) => (a.startMoment?.valueOf() ?? 0) - (b.startMoment?.valueOf() ?? 0))
  const clusters = []
  sorted.forEach((ev) => {
    const start = ev.startMoment?.valueOf() ?? 0
    const end = effectiveEnd(start, ev.endMoment?.valueOf() ?? start)
    const hit = clusters.filter((c) => c.maxEnd > start)
    if (hit.length === 0) {
      clusters.push({ events: [ev], maxEnd: end })
    } else {
      const merged = hit.reduce((acc, c) => {
        clusters.splice(clusters.indexOf(c), 1)
        acc.events.push(...c.events)
        acc.maxEnd = Math.max(acc.maxEnd, c.maxEnd)
        return acc
      }, { events: [ev], maxEnd: end })
      clusters.push(merged)
    }
  })
  return clusters.map((c) => c.events)
}

export const assignColumnsInCluster = (clusterEvents) => {
  const sorted = [...clusterEvents].sort((a, b) => (a.startMoment?.valueOf() ?? 0) - (b.startMoment?.valueOf() ?? 0))
  const colEnds = []
  const assignments = []
  sorted.forEach((ev) => {
    const startVal = ev.startMoment?.valueOf() ?? 0
    const endVal = effectiveEnd(startVal, ev.endMoment?.valueOf() ?? startVal)
    let colIndex = colEnds.findIndex((colEnd) => colEnd <= startVal)
    if (colIndex === -1) { colIndex = colEnds.length; colEnds.push(endVal) }
    else colEnds[colIndex] = endVal
    assignments.push({ ev, colIndex })
  })
  const totalCols = Math.max(colEnds.length, 1)
  return assignments.map(({ ev, colIndex }) => ({ ...ev, colIndex, totalCols }))
}

export const computeEventColumns = (events) => buildOverlapClusters(events).flatMap(assignColumnsInCluster)

export const getEventPosition = (ev, startHour, endHour) => {
  const { startMoment, endMoment } = ev
  if (!startMoment?.isValid() || !endMoment?.isValid()) return null
  const gridEndMins = endHour != null ? (endHour - startHour + 1) * 60 : Infinity
  const startMins = (startMoment.hour() - startHour) * 60 + startMoment.minute()
  const endMins = (endMoment.hour() - startHour) * 60 + endMoment.minute()
  const clampedStart = Math.max(0, startMins)
  const clampedEnd = Math.min(gridEndMins, Math.max(endMins, startMins + 30))
  if (clampedEnd <= clampedStart) return null
  // Multi-day bookings carry no real end-time, so they collapse to the 30-min
  // minimum — too short to show the title. Give them just enough height to fit
  // the time + title rows (without spanning the whole day and overlapping others).
  const height = (clampedEnd - clampedStart) * (HOUR_HEIGHT / 60)
  return {
    top: clampedStart * (HOUR_HEIGHT / 60),
    height: ev.multi_day ? Math.max(height, MIN_MULTI_DAY_HEIGHT) : height,
  }
}

export const getLanesForDay = (eventsWithCols) =>
  eventsWithCols.length > 0 ? Math.max(...eventsWithCols.map((e) => e.totalCols)) : 1

export const getDayMinWidth = (lanes) => Math.max(120, lanes * MIN_LANE_WIDTH)
